import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ListByVehicleResponse } from '../../../store/_declarations/target';
import {
  createSaveQuotationIdHistory,
  listByVehicleApi,
  retrieveQuotationIdData
} from '../../../store/api/financialCalculator';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setQuotationLoading } from '../../../store/slices/financialCalculatorSlice';
import { distributionChannel } from '../../../utils/consts';
import {
  digitalClientMapper,
  salesOrgMapper
} from '../../../utils/targetMappings';
import Spinner from '../../atoms/Spinner';
import WidgetWrapper from '../../atoms/WidgetWrapper';
import PromoCode from '../../molecules/PromoCode';
import ProductCard from '../../organisms/ProductCard';
import { IntroText, ProductListWrapper, SpinnerContainer } from './styles';

const SpinnerComponent = () => (
  <SpinnerContainer data-testid="spinner-main-view">
    <Spinner size={52} />
  </SpinnerContainer>
);

type ListComponentProps = {
  list: ListByVehicleResponse;
  widgetType?: string;
};

const ListComponent = ({ list, widgetType }: ListComponentProps) => {
  const { t } = useTranslation();
  const { montlyRateCalculation } = useAppSelector(
    (state) => state.financialCalculator
  );

  return (
    <ProductListWrapper $widgetType={widgetType}>
      <IntroText>{t('IntroText')}</IntroText>
      <PromoCode />
      {(list?.masterAgreement || []).map((item) => {
        const isLoading = Boolean(
          montlyRateCalculation?.[item.masterAgreement] &&
            montlyRateCalculation?.[item.masterAgreement] === 'loading'
        );
        return (
          <ProductCard
            cardId={item.masterAgreement}
            isLoading={isLoading}
            item={item}
            key={item.masterAgreement}
          />
        );
      })}
    </ProductListWrapper>
  );
};

const MainView: React.FC<{
  closeModal: () => void;
  outputFn?: (output: any) => void;
}> = memo(({ closeModal, outputFn }) => {
  const { consumerPayload, selectedItem, updateWidget, widgetType } =
    useAppSelector((state) => state.app);
  const { status: statusAuth } = useAppSelector((state) => state.auth);
  const { listByVehicle, montlyRateCalculation } = useAppSelector(
    (state) => state.financialCalculator
  );
  const dispatch = useAppDispatch();
  const [isFirstLoad, setIsFirstLoad] = React.useState(true);

  useEffect(() => {
    if (consumerPayload && statusAuth === 'success' && isFirstLoad) {
      dispatch(
        listByVehicleApi({
          context: {
            customerType: consumerPayload.context.customer.clientType,
            digitalClient:
              digitalClientMapper[consumerPayload.context.siteCode],
            distributionChannel,
            salesOrg:
              salesOrgMapper[consumerPayload.context.countryCode.toUpperCase()]
          },
          masterAgreement: {
            isStock: Boolean(consumerPayload.vehicle.materialId),
            vehicleId:
              consumerPayload.vehicle.materialId ||
              consumerPayload.vehicle.manufacturerCode ||
              ''
          }
        })
      );
      const { quotationId } = consumerPayload?.parameters || {};
      if (quotationId) {
        dispatch(retrieveQuotationIdData({ quotationId }));
      } else {
        dispatch(setQuotationLoading(false));
      }

      setIsFirstLoad(false);
    }
  }, [statusAuth, consumerPayload]);

  useEffect(() => {
    if (consumerPayload && updateWidget) {
      /* const pprPayload: DefaultCustomRatePayload = {
        ...consumerPayload
      }; */
      // dispatch(postProductsRate(pprPayload));
    }
  }, [updateWidget]);

  const confirmModal = useCallback(() => {
    if (outputFn) {
      // TODO: adjust callback function
      outputFn({});
      const msc = selectedItem
        ? montlyRateCalculation?.[selectedItem]
        : 'nothing';

      if (selectedItem && msc && typeof msc !== 'string') {
        dispatch(
          createSaveQuotationIdHistory({
            masterAgreement: selectedItem,
            regularPayment: msc.regularPayment
          })
        );
      }
    }
    closeModal();
  }, [closeModal, selectedItem, montlyRateCalculation]);

  const showList = () => {
    if (!listByVehicle) {
      return <SpinnerComponent />;
    }

    return <ListComponent list={listByVehicle} widgetType={widgetType} />;
  };

  return (
    <WidgetWrapper
      hideConfirmButton={!listByVehicle}
      onClickClose={closeModal}
      onClickConfirm={confirmModal}
    >
      {showList()}
    </WidgetWrapper>
  );
});
export default MainView;
